/**
 * Normalises a role or designation name to a lowercase underscore key.
 * "Super Admin", "super_admin", "SUPER ADMIN" → "super_admin"
 */
export function normalizeName(name: string): string {
  return name.toLowerCase().trim().replace(/[\s\-]+/g, '_').replace(/_+/g, '_');
}

/**
 * Maps a user's role + designationName + permissions to a /dashboard/* route.
 * Priority order: role check → designation name → permission fallback → employee default.
 */
export function getDashboardRoute(
  role: string,
  designationName: string,
  permissions: string[],
): string {
  const r = normalizeName(role ?? '');
  if (r === 'super_admin')    return '/admin/dashboard';
  // Operations Manager (one notch below Super Admin) lands on the HR dashboard —
  // its broadest cross-functional operational view. The Super Admin Control Center
  // (/admin/dashboard) stays Super-Admin-only.
  if (r === 'operations_manager') return '/dashboard/hr';
  if (r === 'branch_manager') return '/erp/bm/dashboard';
  if (r === 'erp_admin')      return '/erp/dashboard';

  const n = normalizeName(designationName ?? '');

  // Branch Manager — the same destination the `role === 'branch_manager'` branch above returns.
  // These two used to disagree: role sent you to the real dashboard, designation name sent you to
  // a stub full of hardcoded zeros.
  if (n.includes('branch_manager')) return '/erp/bm/dashboard';

  // Field Technician (exclude "technical support" and "tech support")
  if (
    (n.includes('technician') || n.includes('field_tech')) &&
    !n.includes('technical_support') &&
    !n.includes('tech_support')
  ) return '/technician/dashboard';

  // Backend Support — its own desk, which already exists at /backend-support and is already in
  // their sidebar. Without this branch "Backend Support" normalises to `backend_support`, which
  // matches NONE of the three strings the Technical Support test below looks for, so the whole
  // desk fell through every rule and landed on the generic personal dashboard by accident.
  if (n.includes('backend_support')) return '/backend-support';

  // Technical Support
  if (
    n.includes('technical_support') ||
    n.includes('tech_support') ||
    n.includes('support_executive')
  ) return '/dashboard/technical-support';

  // Technician Manager / Service Manager
  if (
    n.includes('technician_manager') ||
    n.includes('tm_manager') ||
    n.includes('service_manager') ||
    n.includes('installation_manager')
  ) return '/technician/dashboard';

  // Sales & Marketing
  if (
    n.includes('sales') ||
    n.includes('marketing') ||
    n.includes('telecaller') ||
    n.includes('collection_executive') ||
    n.includes('dealer')
  ) return '/dashboard/sales';

  // CRM / Customer Support
  if (n.includes('customer_support') || n.includes('crm')) return '/dashboard/crm';

  // Finance & Accounts
  if (
    n.includes('finance') ||
    n.includes('accountant') ||
    n.includes('accounts_manager') ||
    n.includes('auditor')
  ) return '/dashboard/finance';

  // Warehouse / ERP / Inventory
  if (
    n.includes('warehouse') ||
    n.includes('inventory') ||
    n.includes('procurement') ||
    n.includes('erp')
  ) return '/dashboard/warehouse';

  // HR, Directors, Operations
  if (
    n.includes('hr') ||
    n.includes('human_resource') ||
    n.includes('recruiter') ||
    n.includes('payroll') ||
    n.includes('department_manager') ||
    n.includes('team_lead') ||
    n.includes('it_admin') ||
    n.includes('director') ||
    n.includes('ceo') ||
    n.includes('operations_manager')
  ) return '/dashboard/hr';

  // Permission-based fallback
  if (permissions.some(p => p.startsWith('erp_bm_dashboard'))) return '/erp/bm/dashboard';
  if (permissions.some(p => p.startsWith('tm_manager'))) return '/technician/dashboard';
  if (permissions.some(p => p.startsWith('tm_dashboard'))) return '/technician/dashboard';
  if (permissions.some(p => p.startsWith('sm_dashboard'))) return '/dashboard/sales';
  if (permissions.some(p => p.startsWith('fa_dashboard'))) return '/dashboard/finance';
  if (permissions.some(p => p.startsWith('crm_dashboard'))) return '/dashboard/crm';
  if (permissions.some(p => p.startsWith('ts_dashboard'))) return '/dashboard/technical-support';
  if (permissions.some(p => p.startsWith('erp_dashboard'))) return '/dashboard/warehouse';

  return '/dashboard/employee';
}

/**
 * WHO each dashboard is for, in the words of the designations that land on it.
 *
 * Kept in this file on purpose: `getDashboardRoute` above is what actually sends people to these
 * routes, so the labels and the routing rules are read and edited together. A list of designations
 * held anywhere else would quietly describe a routing table that had since changed.
 *
 * `serves` is prose, not a lookup — designations get renamed and added, and a dashboard header that
 * silently drops a name is worse than one that reads slightly broadly.
 */
export const DASHBOARD_AUDIENCE: Record<string, { title: string; serves: string }> = {
  '/admin/dashboard': {
    title: 'Control Center',
    serves: 'Super Admin',
  },
  '/dashboard/hr': {
    title: 'HR Dashboard',
    serves: 'Operations Manager · HR Director · HR Manager · Payroll Manager · Recruiter · IT Administrator · Directors',
  },
  '/erp/bm/dashboard': {
    title: 'Branch Manager Dashboard',
    serves: 'Branch Manager',
  },
  '/erp/dashboard': {
    title: 'Inventory Management Dashboard',
    serves: 'ERP Admin · Inventory Manager · Warehouse Executive',
  },
  '/dashboard/warehouse': {
    title: 'Warehouse / ERP Dashboard',
    serves: 'Warehouse Executive · Inventory · Procurement',
  },
  '/technician/dashboard': {
    title: 'My Dashboard',
    serves: 'Technician · Senior Technician · Field Service Manager · Service / Installation Manager',
  },
  // A SECOND technician dashboard, a different page, reached only through the Super Admin workspace
  // switcher — workspaceSlice sends technicians here while getDashboardRoute above sends them to
  // /technician/dashboard. The two disagree; consolidating them is deferred, so until then each
  // page says plainly which one it is.
  '/dashboard/technician': {
    title: 'Technician Hub',
    serves: 'Technician workspace preview (Super Admin workspace switcher)',
  },
  '/backend-support': {
    title: 'Backend Support',
    serves: 'Backend Support · Backend Support Lead',
  },
  '/dashboard/technical-support': {
    title: 'Technical Support Dashboard',
    serves: 'Technical Support Executive · Customer Support Executive',
  },
  '/dashboard/crm': {
    title: 'CRM / Customer Support Dashboard',
    serves: 'CRM and customer-support designations',
  },
  '/dashboard/sales': {
    title: 'Sales & Marketing Dashboard',
    serves: 'Sales Manager · Sales Executive · Marketing Lead · Telecaller · Dealer',
  },
  '/dashboard/finance': {
    title: 'Finance & Accounts Dashboard',
    serves: 'Finance Manager · Accountant · Auditor',
  },
  '/dashboard/employee': {
    title: 'My Dashboard',
    serves: 'Everyone — your own attendance, leave, payslips and shifts',
  },
};
